import IdentificationCard from './identificationCard';
import { useTranslation } from 'react-i18next';
import { useFetchIdentifications } from '../../hooks/useFetchIdentifications';
import { useEffect, useMemo, useState } from 'react';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { IFilterOption } from '../../interface/transaction';
import { ICustomerIdentification, IdentificationFilters } from '../../interface/identifications';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import NoData from '../dashboard/noData';
import { TagUserEmpty } from '../icons';
import { Spin } from '../Ant';
import { useInView } from 'react-intersection-observer';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SortSelector from '../sortPopover/sortSelector';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const IdentificationListing = ({ type }: { type: string }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const identificationFilterOptions: IFilterOption[] = [
    {
      label: t('customerIdentification:statusStarted'),
      value: IdentificationFilters.STARTED,
    },
    {
      label: t('customerIdentification:statusNotStarted'),
      value: IdentificationFilters.NOT_STARTED,
    },
    {
      label: t('customerIdentification:statusDone'),
      value: IdentificationFilters.DONE,
    },
  ];
  const initialSearch = searchParams.get('identificationSearch') || '';
  const initialFilterValue = searchParams.get('identificationFilter') || '';
  const initialFilter = identificationFilterOptions.find((opt) => opt.value === initialFilterValue);
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [selectedFilter, setSelectedFilter] = useState<IFilterOption | undefined>(
    initialFilter as IFilterOption,
  );
  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'createdOn',
    direction: 'ASC',
  });

  const sortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'createdOn',
        label: t('customerIdentification:createdOn'),
        type: 'date',
        defaultDirection: 'ASC',
      },
    ],
    [t],
  );
  const {
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    data: paginatedIdentificationsData,
    isLoading: isIdentificationLoading,
  } = useFetchIdentifications({
    relations: ['company:name', 'privatePerson:fullName', 'riskAssessment'],
    search: searchValue,
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    extraFilters: { status: selectedFilter?.value },
    fields: ['status', 'level', 'reviewedOn', 'createdOn'],
    type,
    id,
  });
  useEffect(() => {
    setSearchParams({
      identificationSearch: searchValue,
      identificationFilter: selectedFilter?.value || '',
    });
  }, [searchValue, selectedFilter]);
  const { ref, inView } = useInView({
    threshold: 0.5,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && paginatedIdentificationsData?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="identification-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('customerIdentification:search')}
          filterTitle={t('customerIdentification:filter.label')}
          radioOptions={identificationFilterOptions}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['identifications-list'] });
            return refetch();
          }}
          searchValue={searchValue}
          selectedFilter={selectedFilter ? [selectedFilter] : []}
          setSelectedFilter={(val) => setSelectedFilter(val)}
          clearFilter={() => {
            setSelectedFilter(undefined);
          }}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={sortOptions}
            onApply={(sort) => setActiveSort(sort)}
            activeSort={activeSort}
          />
        </SearchInputWithFilters>
      }
    >
      {Object.entries(paginatedIdentificationsData).length > 0 &&
        paginatedIdentificationsData?.map((val: ICustomerIdentification) => {
          return <IdentificationCard data-testid={`identification-card`} key={val.id} item={val} />;
        })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!paginatedIdentificationsData ||
        (Object.entries(paginatedIdentificationsData).length === 0 && !isFetching)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={t('customerIdentification:noIdentifications')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<TagUserEmpty />}
          />
        </div>
      )}
      {isIdentificationLoading ? <SkeletonCardList rows={[1, 1]} /> : renderLoader()}
    </CollapsibleListLayout>
  );
};

export default IdentificationListing;
